import { Loading } from "@/components/Loading";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InviteService, type Invite } from "@/services/InviteService";
import { UserService, type User, type UserAccess, type UserPermission } from "@/services/UserService";
import { Trash2, UserPlus } from "lucide-react";
import { type ComponentProps, useEffect, useState } from "react";
import { useParams } from "react-router";
import InviteList from "@/components/invites/InviteList";
import SpeciesRegister from "@/components/species/SpeciesRegister";

interface UserPermissionRow {
  user: UserAccess;
  permission: UserPermission;
}

const SiteAdminPage: React.FC = () => {
  const params = useParams();
  const [isLoading, setIsLoading] = useState(true)
  const [users, setUsers] = useState<UserAccess[]>([])
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [invites, setInvites] = useState<Invite[]>([])
  const [removingPermissionKeys, setRemovingPermissionKeys] = useState<Set<string>>(new Set())
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [inviteMode, setInviteMode] = useState<"email" | "code">("email")
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteCode, setInviteCode] = useState("")
  const [isCreatingInvite, setIsCreatingInvite] = useState(false)

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const userPermissionRows = users.map<UserPermissionRow>((user) => ({ user, permission: user.permission }))

  const getSitePermissionKey = (userId: string, siteId: string) => `${userId}-${siteId}`;

  const getPermissionKey = (userId: string, permission: UserPermission) =>
    permission.type === "Site"
      ? getSitePermissionKey(userId, permission.siteId)
      : `${userId}-${permission.type}`;

  const trimmedInviteEmail = inviteEmail.trim();

  const isInviteEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedInviteEmail);

  const trimmedInviteCode = inviteCode.trim();

  const isInviteCodeValid = trimmedInviteCode.length === 9;

  const currentUserEmail = currentUser?.email.trim().toLowerCase() ?? "";
  const isInvitingSelfByEmail = currentUserEmail.length > 0
    && trimmedInviteEmail.toLowerCase() === currentUserEmail;

  const currentUserInviteCode = currentUser?.inviteCode?.trim().toLowerCase() ?? "";
  const isInvitingSelfByCode = currentUserInviteCode.length > 0
    && trimmedInviteCode.toLowerCase() === currentUserInviteCode;

  const isInvitingSelf = inviteMode === "email" ? isInvitingSelfByEmail : isInvitingSelfByCode;

  const isInviteFormValid = currentUser !== null
    && (inviteMode === "email" ? isInviteEmailValid : isInviteCodeValid)
    && !isInvitingSelf;

  const handleInviteDialogOpenChange = (open: boolean) => {
    setIsInviteDialogOpen(open);
    if (!open) {
      setInviteMode("email");
      setInviteEmail("");
      setInviteCode("");
    }
  }

  const handleRemoveInvite = async (invite: Invite) => {
    await InviteService.delete(invite.id);
  }

  const handleCreateInvite: ComponentProps<"form">["onSubmit"] = async (event) => {
    event.preventDefault();

    if (!params.siteId || currentUser === null || !isInviteFormValid || isInvitingSelf) {
      return;
    }

    setIsCreatingInvite(true);
    try {
      const invite = inviteMode === "email"
        ? await InviteService.createByEmail(trimmedInviteEmail, params.siteId)
        : await InviteService.createByCode(trimmedInviteCode, params.siteId);
      setInvites((currentInvites) => [...invite, ...currentInvites]);
      handleInviteDialogOpenChange(false);
    } finally {
      setIsCreatingInvite(false);
    }
  }

  const handleRemovePermission = async (userAccessId: string, siteId: string) => {
    if (!params.siteId) {
      return;
    }

    const permissionKey = getSitePermissionKey(userAccessId, siteId);
    setRemovingPermissionKeys((current) => new Set(current).add(permissionKey));
    try {
      await UserService.removeUserPermissionBySiteId(userAccessId, params.siteId);
      setUsers((currentUsers) =>
        currentUsers.filter((user) =>
          user.id !== userAccessId || user.permission.type !== "Site" || user.permission.siteId !== siteId
        )
      );
    } finally {
      setRemovingPermissionKeys((current) => {
        const next = new Set(current);
        next.delete(permissionKey);
        return next;
      });
    }
  }
    
  useEffect(() => {
    let cancelled = false;

    const loadSiteAdminData = async () => {
      setIsLoading(true);
      setCurrentUser(null);
      try {
        const [usersResult, invitesResult, meResult] = await Promise.allSettled([
          UserService.getUserAccessBySiteId(params.siteId!),
          InviteService.getBySiteId(params.siteId!),
          UserService.getMe(),
        ])
        if (cancelled) {
          return;
        }

        if (usersResult.status === "fulfilled") {
          setUsers(usersResult.value)
        }

        if (invitesResult.status === "fulfilled") {
          setInvites(invitesResult.value)
        }

        if (meResult.status === "fulfilled") {
          setCurrentUser(meResult.value)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadSiteAdminData()

    return () => {
      cancelled = true;
    }
  }, [params.siteId]);

  return(
    <div className="container mx-auto p-4">
      <Loading isLoading={isLoading} message="Loading users..." />
      {!isLoading && (
        <>
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Site Users</h1>
              <p className="text-sm text-gray-600">Manage user permissions for this site.</p>
            </div>
            <Button
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setIsInviteDialogOpen(true)}
            >
              <UserPlus className="h-4 w-4" />
              Invite User
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    User
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    Name
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    Email
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    Permission
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    Access
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {userPermissionRows.map(({ user, permission }) => {
                  const permissionKey = getPermissionKey(user.id, permission);
                  const isRemovingPermission = removingPermissionKeys.has(permissionKey);
                  const isSitePermission = permission.type === "Site";

                  return (
                    <tr
                      key={permissionKey}
                      className="border-t border-gray-200 hover:bg-gray-50"
                    >
                      <td className="py-2 px-4 text-sm text-gray-900">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-blue-100 text-blue-600">
                            {getInitials(user.name || user.email)}
                          </AvatarFallback>
                        </Avatar>
                      </td>
                      <td className="py-2 px-4 text-sm font-medium text-gray-900">
                        {user.name}
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-600">
                        {user.email}
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-900">
                        {permission.type}
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-600">
                        <div className="flex gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${permission.read ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                            Read: {permission.read ? "Yes" : "No"}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${permission.write ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                            Write: {permission.write ? "Yes" : "No"}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 px-4 text-sm text-white">
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isRemovingPermission || !isSitePermission}
                          onClick={() => isSitePermission && handleRemovePermission(user.id, permission.siteId)}
                        >
                          <Trash2 className="h-4 w-4" />
                          {isRemovingPermission ? "Removing..." : "Remove"}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
                {userPermissionRows.length === 0 && (
                  <tr>
                    <td className="py-6 px-4 text-center text-sm text-gray-600" colSpan={6}>
                      No users with permissions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <InviteList invites={invites} setInvites={setInvites} title="Site Invites" instructions="Pending invitations for this site." showAcceptButton={false} onRemove={handleRemoveInvite} />

          {params.siteId && params.tenantId && (
            <SpeciesRegister siteId={params.siteId} tenantId={params.tenantId} />
          )}

          <Dialog open={isInviteDialogOpen} onOpenChange={handleInviteDialogOpenChange}>
            <DialogContent>
              <form onSubmit={handleCreateInvite} className="space-y-4">
                <DialogHeader>
                  <DialogTitle>Invite User</DialogTitle>
                  <DialogDescription>
                    Invite a user to this site by email or invite code.
                  </DialogDescription>
                </DialogHeader>
                {currentUser === null && (
                  <p className="text-sm text-red-600">Unable to verify your account, so invites are disabled.</p>
                )}
                <div className="grid grid-cols-2 gap-2 rounded-lg bg-gray-100 p-1">
                  <Button
                    type="button"
                    variant={inviteMode === "email" ? "default" : "ghost"}
                    className={inviteMode === "email" ? "bg-white text-gray-900 shadow-sm hover:bg-white" : "text-gray-600 hover:text-gray-900"}
                    onClick={() => setInviteMode("email")}
                  >
                    Email
                  </Button>
                  <Button
                    type="button"
                    variant={inviteMode === "code" ? "default" : "ghost"}
                    className={inviteMode === "code" ? "bg-white text-gray-900 shadow-sm hover:bg-white" : "text-gray-600 hover:text-gray-900"}
                    onClick={() => setInviteMode("code")}
                  >
                    Code
                  </Button>
                </div>
                {inviteMode === "email" ? (
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      placeholder="user@example.com"
                      autoComplete="email"
                      aria-invalid={inviteEmail.length > 0 && (!isInviteEmailValid || isInvitingSelfByEmail)}
                    />
                    {isInvitingSelfByEmail ? (
                      <p className="text-sm text-red-600">You cannot invite yourself.</p>
                    ) : inviteEmail.length > 0 && !isInviteEmailValid ? (
                      <p className="text-sm text-red-600">Enter a valid email address.</p>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="invite-code">Invite Code</Label>
                    <Input
                      id="invite-code"
                      value={inviteCode}
                      onChange={(event) => setInviteCode(event.target.value)}
                      placeholder="Enter invite code"
                      aria-invalid={inviteCode.length > 0 && (!isInviteCodeValid || isInvitingSelfByCode)}
                    />
                    {isInvitingSelfByCode ? (
                      <p className="text-sm text-red-600">You cannot invite yourself.</p>
                    ) : inviteCode.length > 0 && !isInviteCodeValid ? (
                      <p className="text-sm text-red-600">Invite code must be 9 characters.</p>
                    ) : null}
                  </div>
                )}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleInviteDialogOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 text-white hover:bg-blue-700"
                    disabled={!isInviteFormValid || isCreatingInvite}
                  >
                    {isCreatingInvite ? "Inviting..." : inviteMode === "email" ? "Invite" : "Invite by Code"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>)
}

export default SiteAdminPage;
